import type { Override } from '@transcend-io/type-utils';

import { UserPreview } from '@main/access-control-types';
import { AssessmentFormPreview } from '@main/attribute-types';
import type { SchemaToType } from '@main/schema-utils';
import { mkInput, mkType } from '@main/schema-utils';

import { AssessmentFormTemplatePreview } from './assessmentFormTemplatePreview';
import { AssessmentLogicInput } from './assessmentLogic';
import { AssessmentTriggerAction, AssessmentTriggerEvent } from './enums';

export const AssessmentTriggerExternalUser = mkType({
  name: 'AssessmentTriggerExternalUser',
  comment: 'The external users associated to an assessment trigger.',
  fields: {
    id: {
      comment: 'The id of the external trigger assignee.',
      type: 'id',
      modelName: 'assessmentTriggerExternalUser',
    },
    email: {
      comment: 'The email of the external assignee.',
      type: 'string',
    },
  },
});

/** Override type */
export type AssessmentTriggerExternalUser = SchemaToType<
  typeof AssessmentTriggerExternalUser
>;

export const AssessmentTriggerInput = mkInput({
  name: 'AssessmentTriggerInput',
  comment: 'Input for the trigger configuration for an assessment',
  fields: {
    action: {
      comment: 'The action to take',
      type: { AssessmentTriggerAction },
    },
    event: {
      comment: 'The event that causes the action',
      type: { AssessmentTriggerEvent },
    },
    targetTemplateId: {
      comment:
        'The id of the template that should be kicked off by this trigger',
      modelName: 'assessmentFormTemplate',
      type: 'id',
      optional: true,
    },
    logic: {
      comment:
        'The trigger logic to evaluate when deciding whether to take the action',
      type: AssessmentLogicInput,
    },
  },
});

/** Override type */
export type AssessmentTriggerInput = SchemaToType<
  typeof AssessmentTriggerInput
>;
export const AssessmentTriggerRaw = mkType({
  name: 'AssessmentTriggerRaw',
  comment: 'The trigger configured for an assessment template',
  fields: {
    id: {
      comment: 'The id of the trigger',
      type: 'id',
      modelName: 'assessmentTrigger',
    },
    action: {
      comment: 'The action to take',
      type: { AssessmentTriggerAction },
    },
    event: {
      comment: 'The event that causes the action',
      type: { AssessmentTriggerEvent },
    },
    targetTemplate: {
      comment: 'The template that should be kicked off by this trigger',
      type: AssessmentFormTemplatePreview,
      optional: true,
    },
    targetForm: {
      comment: 'The secondary form kicked off by this trigger',
      type: AssessmentFormPreview,
      optional: true,
    },
    targetAssignees: {
      comment: 'The users to be assigned to the target form.',
      type: UserPreview,
      optional: true,
      list: true,
    },
    targetExternalAssignees: {
      comment: 'The external users to be assigned to the targer form.',
      type: AssessmentTriggerExternalUser,
      optional: true,
      list: true,
    },
    logic: {
      comment:
        'The trigger logic to evaluate when deciding whether to take the action',
      type: 'string',
    },
  },
});

/** Override type */
export type AssessmentTriggerRaw = SchemaToType<typeof AssessmentTriggerRaw>;

/** Type of the full, parsed AssessmentTrigger; raw and full types are helpful when using `buildUseTransformQuery` */
export type AssessmentTrigger = Override<
  AssessmentTriggerRaw,
  {
    /** parsed `logic` */
    logic: AssessmentLogicInput;
  }
>;
